import React, {useCallback, useEffect, useRef, useState} from 'react';
import styled from 'styled-components';
import {ErrorMessage, Field, useFormikContext} from 'formik';
import {Flex} from 'shared/components/StyledComponents';
import {WYSIWYGEditor} from 'shared/WYSIWYG';
import {Image} from 'components/manufacturer/ManufacturerDefaults/store/manufacturerDefaultsApi';
import Icon from 'shared/Icon';
import Slider from 'components/manufacturer/shared/slider/Slider';
import {isValidNumberKey} from 'shared/helpers/isValidNumberyKey';
import {LabelValue} from 'components/manufacturer/shared/LabelValue';
import ColorPicker from '@cabinetsbycomputer/color-picker';
import ImageField from 'shared/components/Settings/ImageField';
import EditableTextField from 'shared/components/Settings/EditableTextField';

export interface InputFieldType {
    label: string;
    name: string;
    type:
        | 'text'
        | 'password'
        | 'number'
        | 'text-editable'
        | 'text-area'
        | 'select'
        | 'slider'
        | 'rich-text'
        | 'image'
        | 'label-only'
        | 'label-value'
        | 'status'
        | 'color';
    value?: boolean | string | number;
    infoText?: string | JSX.Element;
    customChangeHandler?: React.ChangeEventHandler;
    isHidden?: boolean;
    options?: Array<{
        label: string;
        value: string | number;
        disabled?: boolean;
    }>;
    unit?: string;
    inputWidth?: string;
    inputHeight?: string;
    disabled?: boolean;
    enableCopy?: boolean;
    copyValue?: string;
    alignInput?: string;
    placeholder?: string;
    onSaveField?: (fieldName: string, value: string, values?: unknown) => void;
    onResetField?: (fieldName: string, values?: unknown) => void;
    fileLimit?: number;
    confirmMessage?: {
        message: string | JSX.Element;
        title: string;
        yesButtonName: string;
        noButtonName: string;
    };
}

interface SettingsPanelProps {
    id?: string;
    sectionHeading?: string;
    extraLabel?: string;
    fields: InputFieldType[];
    style?: React.CSSProperties;
    collapsible?: boolean;
    defaultExpanded?: boolean;
    showSectionError?: boolean;
    isCustomer?: boolean;
}

interface GenericType {
    [key: string]: string | boolean | number | Image;
}

const SettingsPanel = ({
    id,
    sectionHeading,
    fields,
    style,
    collapsible,
    defaultExpanded = true,
    showSectionError = false,
    isCustomer = false,
}: SettingsPanelProps) => {
    const {
        values,
        touched,
        errors,
        handleChange,
        setFieldValue,
        setFieldTouched,
        initialValues,
    } = useFormikContext<Partial<GenericType>>();

    const sectionError = showSectionError
        ? fields?.reduce<string | undefined>(
              (message, field) =>
                  message ??
                  (touched[field.name] && typeof errors[field.name] === 'string'
                      ? errors[field.name]
                      : undefined),
              undefined
          )
        : undefined;

    const expandedContentRef = useRef<HTMLDivElement>(null);
    const [expanded, setExpanded] = useState(defaultExpanded);
    const expandedRef = useRef(expanded);
    const toggleExpanded = useCallback(() => {
        if (collapsible) {
            setExpanded((prevState) => !prevState);
        }
    }, []);
    useEffect(() => {
        if (expanded && expandedRef.current != expanded && collapsible) {
            expandedContentRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'end',
            });
        }
        expandedRef.current = expanded;
    }, [expanded, collapsible]);

    const setValue = useCallback(
        (fieldName: string) => (value: string) => {
            void setFieldValue(fieldName, value);
            void setFieldTouched(fieldName, true);
        },
        []
    );
    const setTouched = useCallback(
        (fieldName: string) => () => {
            if (!Object.keys(touched).includes(fieldName)) {
                void setFieldTouched(fieldName, true);
            }
        },
        [touched]
    );

    const resetImage = useCallback(
        (fieldName: string, defaultImageUrl: string) => () => {
            void setFieldValue(fieldName, {
                id: 0,
                imageUrl: defaultImageUrl,
                isDefault: true,
            });
        },
        []
    );

    const handleNumberInput = useCallback(
        (event: React.KeyboardEvent<HTMLInputElement>) => {
            if (!isValidNumberKey(event)) {
                event.preventDefault();
            }
        },
        []
    );

    const preventWheelEvent = useCallback(
        (event: React.WheelEvent<HTMLInputElement>) => {
            event.currentTarget.blur();
        },
        []
    );

    return (
        <>
            {sectionHeading ? (
                <SubHeader
                    id={id}
                    $collapsible={collapsible}
                    onClick={toggleExpanded}>
                    {sectionHeading}
                    {collapsible ? (
                        <Icon
                            key={`icon-${expanded ? 'up' : 'down'}`}
                            width="15px"
                            iconName={
                                expanded
                                    ? 'ChevronUpWhite.svg'
                                    : 'ChevronDownWhite.svg'
                            }
                            color="white"
                        />
                    ) : null}
                </SubHeader>
            ) : null}
            <div ref={expandedContentRef}>
                {expanded ? (
                    <SectionContainer style={style}>
                        {sectionError ? (
                            <SectionError>{sectionError}</SectionError>
                        ) : null}
                        {fields?.map((field) => {
                            if (field.isHidden) {
                                return null;
                            }
                            // NOTE: This won't work on nested values
                            // need to use useField() hook
                            // render each fields in separate component
                            // and use useField() hook from formik to
                            // handle nested field values properly
                            const fieldValue = values[field.name];
                            let onChangeHandler = handleChange;
                            if (!!field?.customChangeHandler) {
                                onChangeHandler = field?.customChangeHandler;
                            }

                            let onSaveFieldHandler = undefined;
                            if (!!field?.onSaveField) {
                                onSaveFieldHandler = () => {
                                    field?.onSaveField?.(
                                        field.name,
                                        fieldValue as string,
                                        values
                                    );
                                    void setFieldTouched(field.name, false);
                                };
                            }

                            let onResetFieldHandler = undefined;
                            let showColorPickerResetDefault = false;
                            if (!!field?.onResetField) {
                                onResetFieldHandler = () => {
                                    field?.onResetField?.(field.name, values);
                                    void setFieldTouched(field.name, false);
                                    void setFieldValue(
                                        field.name,
                                        initialValues[field.name]
                                    );
                                };
                                showColorPickerResetDefault = isCustomer
                                    ? initialValues[field.name] !== fieldValue
                                    : initialValues[field.name] !== '';
                            }

                            let imageValue: Image;
                            let displayImageManager = undefined;
                            if (field.type === 'image') {
                                imageValue = fieldValue as Image;
                                displayImageManager = () => {
                                    // eslint-disable-next-line @typescript-eslint/no-unsafe-call -- function below is generated from goCabinets
                                    showImageManager((value: Image) => {
                                        void setFieldValue(field.name, {
                                            ...value,
                                            defaultImageUrl:
                                                imageValue?.isDefault
                                                    ? imageValue?.imageUrl
                                                    : imageValue?.defaultImageUrl,
                                        });
                                        if (!!field?.onSaveField) {
                                            field?.onSaveField?.(
                                                field.name,
                                                value
                                            );
                                        }
                                    });
                                };
                            }

                            return (
                                <React.Fragment
                                    key={
                                        !!field.name ? field.name : field.label
                                    }>
                                    {field.type === 'label-only' ? (
                                        <InputGroup>
                                            <strong
                                                style={{
                                                    fontSize: 16,
                                                    paddingTop: 10,
                                                }}>
                                                {field.label}
                                            </strong>
                                        </InputGroup>
                                    ) : null}
                                    {field.type === 'label-value' ? (
                                        <LabelValue field={field} />
                                    ) : null}
                                    {field.type === 'slider' ? (
                                        <InputGroup>
                                            <SettingContainer>
                                                <label>{field.label}</label>
                                                {!!field.infoText ? (
                                                    <p>{field.infoText}</p>
                                                ) : null}
                                            </SettingContainer>
                                            <Flex
                                                $alignItems={
                                                    field?.alignInput ||
                                                    undefined
                                                }
                                                $flexDirection="column"
                                                $width={field?.inputWidth}>
                                                <Slider
                                                    name={field.name}
                                                    value={
                                                        fieldValue as boolean
                                                    }
                                                    handleChange={
                                                        onChangeHandler
                                                    }
                                                    handleBlur={setTouched(
                                                        field.name
                                                    )}
                                                    isLoading={false}
                                                    AvailableText={''}
                                                    HiddenText={''}
                                                />
                                                {!showSectionError ? (
                                                    <ErrorMessage
                                                        component={StyledError}
                                                        name={field.name}
                                                    />
                                                ) : null}
                                            </Flex>
                                        </InputGroup>
                                    ) : null}
                                    {field.type === 'select' ? (
                                        <InputGroup>
                                            <SettingContainer>
                                                <label>{field.label}</label>
                                                {!!field.infoText ? (
                                                    <p>{field.infoText}</p>
                                                ) : null}
                                            </SettingContainer>
                                            <SettingContainer
                                                style={{alignItems: 'end'}}>
                                                <Field
                                                    as={StyledSelect}
                                                    aria-label={field.name}
                                                    name={field.name}
                                                    disabled={field.disabled}>
                                                    {field?.options.map(
                                                        ({
                                                            label,
                                                            value,
                                                            disabled = false,
                                                        }) => {
                                                            return (
                                                                <option
                                                                    key={label}
                                                                    value={
                                                                        value
                                                                    }
                                                                    disabled={
                                                                        disabled
                                                                    }>
                                                                    {label}
                                                                </option>
                                                            );
                                                        }
                                                    )}
                                                </Field>
                                                <ErrorMessage
                                                    component={StyledError}
                                                    name={field.name}
                                                />
                                            </SettingContainer>
                                        </InputGroup>
                                    ) : null}
                                    {['text', 'number', 'password'].includes(
                                        field.type
                                    ) ? (
                                        <InputGroup>
                                            <SettingContainer>
                                                <label>{field.label}</label>
                                                {!!field.infoText ? (
                                                    <p>{field.infoText}</p>
                                                ) : null}
                                            </SettingContainer>
                                            <SettingContainer
                                                style={{alignItems: 'end'}}>
                                                <Flex
                                                    $gap="5px"
                                                    $alignItems="center">
                                                    <StyledInput
                                                        $width={
                                                            field?.inputWidth
                                                        }
                                                        aria-label={field.name}
                                                        name={field.name}
                                                        type={field.type}
                                                        autoComplete="off"
                                                        placeholder={
                                                            field.placeholder
                                                        }
                                                        onChange={
                                                            onChangeHandler
                                                        }
                                                        onKeyDown={
                                                            field.type ===
                                                            'number'
                                                                ? handleNumberInput
                                                                : undefined
                                                        }
                                                        onWheel={
                                                            field.type ===
                                                            'number'
                                                                ? preventWheelEvent
                                                                : undefined
                                                        }
                                                    />
                                                    {field?.unit ? (
                                                        <span
                                                            style={{
                                                                minWidth: 30,
                                                            }}>
                                                            {field?.unit}
                                                        </span>
                                                    ) : null}
                                                </Flex>
                                                <ErrorMessage
                                                    component={StyledError}
                                                    name={field.name}
                                                />
                                            </SettingContainer>
                                        </InputGroup>
                                    ) : null}
                                    {field.type === 'text-editable' ? (
                                        <EditableTextField
                                            field={field}
                                            onSaveFieldHandler={
                                                onSaveFieldHandler
                                            }
                                        />
                                    ) : null}
                                    {field.type === 'rich-text' ? (
                                        <InputGroup $flexDirection="column">
                                            <SettingContainer>
                                                <label>{field.label}</label>
                                                {!!field.infoText ? (
                                                    <p>{field.infoText}</p>
                                                ) : null}
                                            </SettingContainer>
                                            <SettingContainer
                                                style={{background: 'white'}}>
                                                <WYSIWYGEditor
                                                    testId={field.name}
                                                    inputText={
                                                        fieldValue as string
                                                    }
                                                    setInputText={setValue(
                                                        field.name
                                                    )}
                                                />
                                            </SettingContainer>
                                        </InputGroup>
                                    ) : null}
                                    {field.type === 'text-area' ? (
                                        <InputGroup>
                                            <SettingContainer>
                                                <label>{field.label}</label>
                                                {!!field.infoText ? (
                                                    <p>{field.infoText}</p>
                                                ) : null}
                                            </SettingContainer>
                                            <SettingContainer
                                                style={{alignItems: 'end'}}>
                                                <Field
                                                    as={StyledTextarea}
                                                    aria-label={field.name}
                                                    name={field.name}
                                                    autoComplete="off"
                                                />
                                                <ErrorMessage
                                                    component={StyledError}
                                                    name={field.name}
                                                />
                                            </SettingContainer>
                                        </InputGroup>
                                    ) : null}
                                    {field.type === 'image' ? (
                                        <ImageField
                                            field={field}
                                            imageValue={imageValue}
                                            isCustomer={isCustomer}
                                            onResetDefault={
                                                !!onResetFieldHandler
                                                    ? onResetFieldHandler
                                                    : resetImage(
                                                          field.name,
                                                          imageValue?.defaultImageUrl
                                                      )
                                            }
                                            onThumbailClick={
                                                displayImageManager
                                            }
                                        />
                                    ) : null}
                                    {field.type === 'status' ? (
                                        <InputGroup>
                                            <SettingContainer>
                                                <label>{field.label}</label>
                                                {!!field.infoText ? (
                                                    <p>{field.infoText}</p>
                                                ) : null}
                                            </SettingContainer>
                                            <SettingContainer
                                                style={{
                                                    justifyContent: 'center',
                                                }}>
                                                <ConnectionStatus
                                                    data-cy="status-field"
                                                    $status={!!field.value}>
                                                    {field.value
                                                        ? 'Connected'
                                                        : 'Disconnected'}
                                                </ConnectionStatus>
                                            </SettingContainer>
                                        </InputGroup>
                                    ) : null}
                                    {field.type === 'color' ? (
                                        <InputGroup
                                            style={{alignItems: 'center'}}>
                                            <SettingContainer $width="60%">
                                                <label>{field.label}</label>
                                                {!!field.infoText ? (
                                                    typeof field.infoText ===
                                                    'string' ? (
                                                        <p>{field.infoText}</p>
                                                    ) : (
                                                        field.infoText
                                                    )
                                                ) : null}
                                            </SettingContainer>
                                            <Flex
                                                $flexDirection="column"
                                                $gap="5px"
                                                $alignItems="end">
                                                <SettingContainer $flexDirection="row">
                                                    <ColorPicker
                                                        color={String(
                                                            fieldValue
                                                        )}
                                                        name={field.name}
                                                        onChange={setValue(
                                                            field.name
                                                        )}
                                                        onSaveCallback={
                                                            onSaveFieldHandler
                                                        }
                                                    />
                                                </SettingContainer>
                                                <ErrorMessage
                                                    component={StyledError}
                                                    name={field.name}
                                                />
                                                {showColorPickerResetDefault ? (
                                                    <ResetDefault
                                                        onClick={
                                                            onResetFieldHandler
                                                        }>
                                                        <Icon
                                                            width="16px"
                                                            iconName="Reverse-Refresh.svg"
                                                        />
                                                        Default
                                                    </ResetDefault>
                                                ) : null}
                                            </Flex>
                                        </InputGroup>
                                    ) : null}
                                </React.Fragment>
                            );
                        })}
                    </SectionContainer>
                ) : null}
            </div>
        </>
    );
};

export const InputGroup = styled.div<{$flexDirection?: string}>`
    display: flex;
    justify-content: space-between;
    position: relative;
    padding-bottom: 10px;
    border-bottom: 1px solid #e3e6e8;
    & label {
        font-weight: bold;
        font-size: 0.875rem;
        color: #4d4d4d;
    }
    gap: 10px;
    flex-direction: ${({$flexDirection}) => $flexDirection};
`;

export const SettingContainer = styled.div<{
    $flexDirection?: string;
    $minWidth?: string;
    $width?: string;
}>`
    display: flex;
    flex-direction: ${({$flexDirection = 'column'}) => $flexDirection};
    gap: 5px;
    min-width: ${({$minWidth}) => $minWidth};
    width: ${({$width}) => $width};
    & > p {
        font-size: 13px;
        white-space: pre-line;
        line-height: 1.5;
        margin: 0;
    }
`;

export const StyledInput = styled(Field)<{
    $width?: string;
    $textAlign?: string;
}>`
    &,
    &[type='number'] {
        padding: 5px 10px;
        width: ${({$width = '65px'}) => $width};
        border-radius: 5px;
        text-align: ${({$textAlign = 'left'}) => $textAlign};
        box-shadow: none !important;
        background-color: white;
        border: solid #999 1px;
        font-size: 0.8rem;
        margin-bottom: auto;
        height: 27px;
        line-height: normal;
        &:focus {
            border-color: ${({theme}) => theme.colors.primary.main} !important;
        }
    }
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }
    &[type='number'] {
        height: 27px;
        border-color: #999;
        -moz-appearance: textfield;
    }
`;

export const StyledError = styled.div<{$mt?: string; $ml?: string}>`
    color: red;
    margin-left: ${({$ml = '5px'}) => $ml};
    margin-top: ${({$mt = '0'}) => $mt};
    font-size: 12px;
`;

const SectionError = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    border-radius: 5px;
    background: #fdecea;
    border: 1px solid #f5c2c0;
    color: #b3261e;
    font-size: 13px;
    font-weight: 600;
    &:before {
        content: '!';
        flex: 0 0 auto;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 16px;
        height: 16px;
        border-radius: 50%;
        background: #b3261e;
        color: #fff;
        font-size: 11px;
        font-weight: 700;
    }
`;

export const SectionContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 0 10px 15px;
`;

const StyledSelect = styled.select`
    padding: 0 10px;
    width: 185px;
    border-radius: 5px;
    &:focus {
        border-color: ${({theme}) => theme.colors.primary.main} !important;
        box-shadow: none !important;
    }
`;

const StyledTextarea = styled.textarea`
    padding: 5px 10px;
    width: 250px;
    border-radius: 5px;
    resize: vertical;
    &:focus {
        border-color: ${({theme}) => theme.colors.primary.main} !important;
        box-shadow: none !important;
    }
`;

export const SubHeader = styled.div<{$collapsible?: boolean}>`
    font-weight: 700;
    background: ${({theme}) => theme.colors.primary.main};
    padding: 8px 10px;
    color: #ffffff;
    border-radius: 24px;
    border-bottom-right-radius: 0;
    margin: 15px 0;
    font-size: 17px;
    max-height: 40px;
    ${({$collapsible}) => {
        if ($collapsible) {
            return `
                display: flex;
                justify-content: space-between;
                &:hover {
                    cursor: pointer;
                }
            `;
        }
    }}
`;

const ResetDefault = styled.div`
    display: flex;
    gap: 5px;
    &:hover {
        cursor: pointer;
        color: ${({theme}) => theme.colors.primary.main};
    }
`;

const ConnectionStatus = styled.div<{$status: boolean}>`
    font-size: 18px;
    font-weight: bold;
    display: inline-flex;
    align-items: center;
    gap: 7px;
    color: ${({$status}) => ($status ? 'green' : 'red')};
    &:before {
        content: '';
        display: inline-block;
        width: 12px;
        height: 12px;
        background-color: ${({$status}) => ($status ? 'green' : 'red')};
        border-radius: 50%;
    }
`;

export const isValidHexColor = (value: string) => {
    return /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(value);
};

export default SettingsPanel;
